import * as React from "react";
import { useCallback, useRef } from "react";
import * as Immutable from "immutable";
import { Field } from "formik";
import styled from "styled-components";
import moment from "moment";
import useView from "views/hooks/useView";
import { useStore } from "stores/connect";
import { Spinner } from "components/common";
import SearchButton from "views/components/searchbar/SearchButton";
import SearchActionsMenu from "views/components/searchbar/saved-search/SearchActionsMenu";
import TimeRangeFilter from "views/components/searchbar/time-range-filter";
import ViewsQueryInput from "views/components/searchbar/ViewsQueryInput";
import ViewsRefreshControls from "views/components/searchbar/ViewsRefreshControls";
import ScrollToHint from "views/components/common/ScrollToHint";
import { StreamsStore } from "views/stores/StreamsStore";
import ViewsQueryValidation from "views/components/searchbar/queryvalidation/ViewsQueryValidation";
import {
  createElasticsearchQueryString,
  filtersToStreamSet,
  filtersToStreamCategorySet,
  newFiltersForQuery
} from "views/logic/queries/Query";
import { QUERY_TIME_RANGE_LIMIT_ERROR_TYPE } from "views/Constants";
import WidgetFocusContext from "views/components/contexts/WidgetFocusContext";
import FormWarningsContext from "contexts/FormWarningsContext";
import FormWarningsProvider from "contexts/FormWarningsProvider";
import debounceWithPromise from "views/logic/debounceWithPromise";
import validateQuery from "views/components/searchbar/queryvalidation/validateQuery";
import usePluginEntities from "hooks/usePluginEntities";
import PluggableSearchBarControls from "views/components/searchbar/PluggableSearchBarControls";
import useParameters from "views/hooks/useParameters";
import ValidateOnParameterChange from "views/components/searchbar/ValidateOnParameterChange";
import useUserDateTime from "hooks/useUserDateTime";
import {
  SEARCH_BAR_GAP,
  TimeRangeRow,
  SearchBarContainer,
  SearchQueryRow,
  SearchButtonAndQuery,
  SearchInputAndValidationContainer
} from "views/components/searchbar/SearchBarLayout";
import PluggableCommands from "views/components/searchbar/queryinput/PluggableCommands";
import useCurrentQuery from "views/logic/queries/useCurrentQuery";
import useQueryFilters from "views/logic/queries/useQueryFilters";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { updateQuery, executeActiveQuery } from "views/logic/slices/viewSlice";
import useHandlerContext from "views/components/useHandlerContext";
import QueryHistoryButton from "views/components/searchbar/QueryHistoryButton";
import useIsLoading from "views/hooks/useIsLoading";
import useSearchConfiguration from "hooks/useSearchConfiguration";
import { defaultCompare } from "logic/DefaultCompare";
import useAutoRefresh from "views/hooks/useAutoRefresh";
import useViewsSelector from "views/stores/useViewsSelector";
import { selectCurrentQueryResults } from "views/logic/slices/viewSelectors";
import useSearchResultTimeRangeErrorCheck from "views/hooks/useSearchResultTimeRangeErrorCheck";
import StreamsFilter from "views/components/searchbar/StreamsFilter";
import SearchBarForm from "./searchbar/SearchBarForm";
import {
  executeSearchSubmitHandler as executePluggableSubmitHandler,
  useInitialSearchValues as usePluggableInitialValues,
  pluggableValidationPayload
} from "../logic/searchbar/pluggableSearchBarControlsHandler";
const StreamsAndRefresh = styled.div`
  display: flex;
  gap: ${SEARCH_BAR_GAP};
  flex: 1.5;
`;
const defaultOnSubmit = async (dispatch, values, pluggableSearchBarControls, currentQuery, restartAutoRefresh) => {
  const { timerange, streamsAndCategories, queryString } = values;
  restartAutoRefresh();
  const queryWithPluginData = await executePluggableSubmitHandler(
    dispatch,
    values,
    pluggableSearchBarControls,
    currentQuery
  );
  const newQuery = queryWithPluginData.toBuilder().timerange(timerange).filter(newFiltersForQuery(streamsAndCategories == null ? void 0 : streamsAndCategories.streams, streamsAndCategories == null ? void 0 : streamsAndCategories.categories)).query(createElasticsearchQueryString(queryString)).build();
  if (!currentQuery.equals(newQuery)) {
    return dispatch(updateQuery(newQuery.id, newQuery));
  }
  return dispatch(executeActiveQuery());
};
const defaultProps = {
  onSubmit: defaultOnSubmit
};
const debouncedValidateQuery = debounceWithPromise(validateQuery, 350);
const useInitialFormValues = ({
  currentQuery,
  queryFilters
}) => {
  const { id, query, timerange } = currentQuery != null ? currentQuery : {};
  const { query_string: queryString } = query != null ? query : {};
  const initialValuesFromPlugins = usePluggableInitialValues(currentQuery);
  const streams = filtersToStreamSet(queryFilters.get(id, Immutable.Map())).toJS();
  const streamCategories = filtersToStreamCategorySet(queryFilters.get(id, Immutable.Map())).toJS();
  const streamsAndCategories = {
    streams,
    categories: streamCategories
  };
  return { timerange, streamsAndCategories, queryString, ...initialValuesFromPlugins };
};
const _validateQueryString = (values, pluggableSearchBarControls, userTimezone, context) => {
  var _a, _b;
  const request = {
    timeRange: values == null ? void 0 : values.timerange,
    streams: (_a = values == null ? void 0 : values.streamsAndCategories) == null ? void 0 : _a.streams,
    streamCategories: (_b = values == null ? void 0 : values.streamsAndCategories) == null ? void 0 : _b.categories,
    queryString: values == null ? void 0 : values.queryString,
    ...pluggableValidationPayload(values, context, pluggableSearchBarControls)
  };
  return debouncedValidateQuery(request, userTimezone);
};
const SearchBar = ({ onSubmit = defaultProps.onSubmit, scrollContainer }) => {
  var _a;
  const editorRef = useRef(null);
  const view = useView();
  const availableStreams = useStore(
    StreamsStore,
    ({ streams }) => streams.map((stream) => ({
      key: stream.title,
      value: stream.id
    }))
  );
  const availableStreamCategories = useStore(
    StreamsStore,
    ({ streams }) => streams.flatMap((stream) => {
      if (stream.categories) {
        return stream.categories.map((s) => ({ key: s, value: s }));
      }
      return [];
    }).filter((element, index, self) => index === self.findIndex((e) => e.value === element.value)).sort((a, b) => defaultCompare(a.value, b.value))
  );
  const { config } = useSearchConfiguration();
  const { userTimezone } = useUserDateTime();
  const { parameters } = useParameters();
  const currentQuery = useCurrentQuery();
  const queryFilters = useQueryFilters();
  const results = useViewsSelector(selectCurrentQueryResults);
  const pluggableSearchBarControls = usePluginEntities("views.components.searchBar");
  const initialValues = useInitialFormValues({ queryFilters, currentQuery });
  const dispatch = useViewsDispatch();
  const { restartAutoRefresh } = useAutoRefresh();
  const _onSubmit = useCallback(
    (values) => onSubmit(dispatch, values, pluggableSearchBarControls, currentQuery, restartAutoRefresh),
    [currentQuery, dispatch, onSubmit, pluggableSearchBarControls, restartAutoRefresh]
  );
  const handlerContext = useHandlerContext();
  const isLoadingExecution = useIsLoading();
  const searchResultTimeRangeErrorCheck = useSearchResultTimeRangeErrorCheck(QUERY_TIME_RANGE_LIMIT_ERROR_TYPE);
  if (!currentQuery || !config) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const { query } = currentQuery;
  const limitDuration = (_a = moment.duration(config.query_time_range_limit).asSeconds()) != null ? _a : 0;
  return /* @__PURE__ */ React.createElement(WidgetFocusContext.Consumer, null, ({ focusedWidget: { editing } = { editing: false } }) => /* @__PURE__ */ React.createElement(FormWarningsProvider, null, /* @__PURE__ */ React.createElement(
    SearchBarForm,
    {
      initialValues,
      limitDuration,
      onSubmit: _onSubmit,
      validateQueryString: (values) => _validateQueryString(values, pluggableSearchBarControls, userTimezone, handlerContext)
    },
    ({
      dirty,
      errors,
      isSubmitting,
      isValid,
      isValidating,
      handleSubmit,
      values,
      setFieldValue,
      validateForm
    }) => {
      const showTimeRangeErrorFromResults = searchResultTimeRangeErrorCheck(values == null ? void 0 : values.timerange);
      const disableSearchSubmit = isSubmitting || isValidating || !isValid || isLoadingExecution || showTimeRangeErrorFromResults;
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ValidateOnParameterChange, { parameters }), /* @__PURE__ */ React.createElement(
        ScrollToHint,
        {
          scrollContainer,
          ifValueChanges: query.query_string,
          title: "Scroll to search bar"
        }
      ), /* @__PURE__ */ React.createElement(SearchBarContainer, null, /* @__PURE__ */ React.createElement(TimeRangeRow, null, /* @__PURE__ */ React.createElement(
        TimeRangeFilter,
        {
          limitDuration,
          onChange: (nextTimeRange) => setFieldValue("timerange", nextTimeRange),
          value: values == null ? void 0 : values.timerange,
          hasErrorOnMount: !!errors.timerange || showTimeRangeErrorFromResults,
          moveRangeProps: {
            effectiveTimerange: results == null ? void 0 : results.effectiveTimerange,
            initialTimerange: currentQuery.timerange,
            initialTimerangeFormat: "internalIndexer"
          }
        }
      ), /* @__PURE__ */ React.createElement(StreamsAndRefresh, null, /* @__PURE__ */ React.createElement(Field, { name: "streamsAndCategories" }, ({ field: { name, value, onChange } }) => /* @__PURE__ */ React.createElement(
        StreamsFilter,
        {
          value: { streams: value == null ? void 0 : value.streams, categories: value == null ? void 0 : value.categories },
          streams: availableStreams,
          streamCategories: availableStreamCategories,
          onChange: (selected) => {
            onChange({
              target: {
                value: selected,
                name
              }
            });
          }
        }
      )), /* @__PURE__ */ React.createElement(ViewsRefreshControls, { disable: !isValid }))), /* @__PURE__ */ React.createElement(SearchQueryRow, null, /* @__PURE__ */ React.createElement(SearchButtonAndQuery, null, /* @__PURE__ */ React.createElement(
        SearchButton,
        {
          disabled: disableSearchSubmit,
          dirty,
          displaySpinner: isSubmitting || isLoadingExecution
        }
      ), /* @__PURE__ */ React.createElement(SearchInputAndValidationContainer, null, /* @__PURE__ */ React.createElement(Field, { name: "queryString" }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(FormWarningsContext.Consumer, null, ({ warnings }) => /* @__PURE__ */ React.createElement(PluggableCommands, { usage: "search_query" }, (customCommands) => {
        var _a2;
        return /* @__PURE__ */ React.createElement(
          ViewsQueryInput,
          {
            value,
            ref: editorRef,
            view,
            timeRange: values.timerange,
            streams: (_a2 = values.streamsAndCategories) == null ? void 0 : _a2.streams,
            name,
            onChange,
            placeholder: 'Type your search query here and press enter. E.g.: ("not found" AND http) OR http_response_code:[400 TO 404]',
            error,
            isValidating,
            warning: warnings.queryString,
            disableExecution: disableSearchSubmit,
            validate: validateForm,
            onExecute: handleSubmit,
            commands: customCommands
          }
        );
      }))), /* @__PURE__ */ React.createElement(ViewsQueryValidation, null), /* @__PURE__ */ React.createElement(QueryHistoryButton, { editorRef }))), !editing && /* @__PURE__ */ React.createElement(SearchActionsMenu, null)), /* @__PURE__ */ React.createElement(PluggableSearchBarControls, null)));
    }
  )));
};
export default SearchBar;
